import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Box, CircularProgress } from '@material-ui/core';
import Grid24 from '../../../../UI/Grid24';
import DateField from '../../../../UI/Field/Date/DateField';
import FieldModel, { DATE } from '../../../../../models/FieldModel';
import {
  downloadFileSispag, getMoneyMask, hasUserPermissions, toAmericanDate, toBrazilianDate,
} from '../../../../../helpers';
import Radio from '../../../../UI/Field/Radio/RadioField';
import MoneyField from '../../../../UI/Field/Money/MoneyField';
import NumberInput from '../../../../UI/Field/Number/NumberInput';
import AlphanumericMaskField from '../../../../UI/Field/Mask/AlphanumericMaskField';
import { CNPJ_MASK, CPF_MASK } from '../../../../../helpers/constants/layout.constants';
import InputField from '../../../../UI/Field/Input';
import SelectorField from '../../../../UI/Field/Selector';
import { findEstadosAction, findFasesAction, findMotivosBaixaAction } from '../../../../../store/Global/LoadSelectors/loadSelectors.saga';
import SearchButtonWithLabel from '../../../../UI/Button/Search/Label/SearchButtonWithLabel';
import { ClearButton } from '../../../../AtualizarMuralDashboard/atualizarMuralDashboard.styles';
import { buscarIdFaseBaixa } from '../../../../../services/core/fase/fase.services';
import { buscarFichaCliente, exportarFichaCliente } from '../../../../../services/core/cliente/cliente.services';
import { buscarAcompanhamentoDoProcessoService } from '../../../../../services/core/processos/processosService';
import { AcompanhamentoProcesso } from '../../../../../pages/InspecionarProcesso/Acompanhamento/Acompanhamento';
import DialogNew from '../../../../../layouts/FormDialog/DialogNew';
import { FacilitadoresButton } from '../../../../../pages/Dashboard/Dashboard.style';
import { tableFichaClienteFactory } from './TableFichaClienteFactory';
import TableFichaCliente from './TableFichaCliente';
import { ActionsContainer, FieldsContainer } from './TableFichaClienteStyle';
import WrapperSubTitleForm from '../../../../UI/WrapperSubTitleForm';
import { GroupDefinitions } from '../BaseCalculo/Honorarios/styles';


function FichaDoCliente({
  idCliente,
  isInsertMode,
}) {
  const globalDispatch = useDispatch();

  const permission = React.useMemo(() => {
    const dashboardGerenciar = hasUserPermissions(1401);
    const ccClienteConsultar = hasUserPermissions(1801);
    const ccClienteConsultarTodos = hasUserPermissions(1802);
    const ccClienteEfetuarLancamentos = hasUserPermissions(1803);
    const ccClientePrestarContas = hasUserPermissions(1804);
    const hasAnyPermissionCCCliente = ccClienteConsultar
      || ccClienteConsultarTodos
      || ccClienteEfetuarLancamentos
      || ccClientePrestarContas;
    return {
      dashboardGerenciar,
      ccClienteConsultar,
      ccClienteConsultarTodos,
      ccClienteEfetuarLancamentos,
      ccClientePrestarContas,
      hasAnyPermissionCCCliente,
    };
  }, []);

  const [dataInicio, setDataInicio] = useState(
    new FieldModel({ name: 'dataEnvioInicial', type: DATE, value: toBrazilianDate(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)) }),
  );
  const [valorFinal, setValorFinal] = useState(
    new FieldModel({ name: 'valorFinal', value: '9999999999.99' }),
  );
  const [valorInicial, setValorInicial] = useState(
    new FieldModel({ name: 'valorInicial', value: '0' }),
  );
  const [nroProcesso, setNroProcesso] = useState(
    new FieldModel({ name: 'nroProcesso', value: '' }),
  );
  const [idDevedor, setIdDevedor] = useState(
    new FieldModel({ name: 'idDevedor', value: '' }),
  );
  const [codNoCliente, setCodNoCliente] = useState(
    new FieldModel({ name: 'codNoCliente', value: '' }),
  );
  const [cpfCnpjDevedor, setCpfCnpjDevedor] = useState(
    new FieldModel({ name: 'cpfCnpjDevedor', value: '' }),
  );
  const [isCnpj, setIsCnpj] = useState(false);
  const [nomeDevedor, setNomeDevedor] = useState(
    new FieldModel({ name: 'nomeDevedor', value: '' }),
  );
  const [cidadeDevedor, setCidadeDevedor] = useState(
    new FieldModel({ name: 'cidadeDevedor', value: '' }),
  );
  const [idEstado, setIdEstado] = useState(
    new FieldModel({ name: 'idEstado', value: '' }),
  );
  const [idFase, setIdFase] = useState(
    new FieldModel({ name: 'idFase', value: '' }),
  );
  const [idMotivoBaixa, setIdMotivoBaixa] = useState(
    new FieldModel({ name: 'idMotivoBaixa', value: '' }),
  );
  const [idFaseBaixa, setIdFaseBaixa] = useState(null);

  const [listaFichaCliente, setListaFichaCliente] = useState([]);
  const [dialogAcompanhamento, setDialogAcompanhamento] = useState({
    open: false,
    loading: false,
    dados: {},
    titulo: '',
  });

  const estados = useSelector(states => states.selectors.estados.selector);
  const fases = useSelector(state => state.selectors.fases.selector);
  const motivosBaixa = useSelector(states => states.selectors.motivosBaixa.selector);

  const radioOptions = {
    TODOS: 'todos',
    FASE_ESPECIFICA: 'faseEspecifica',
    PROCESSOS_ATIVOS: 'processosAtivos',
    PROCESSOS_BAIXADOS: 'processosBaixados',
  };

  const [definition, setDefinition] = useState(radioOptions.TODOS);


  const makeConteudoTabela = useCallback((fichaCliente) => {
    if (!fichaCliente || fichaCliente.length === 0) {
      return [];
    }

    return {
      id: fichaCliente.processo,
      nomeDoDevedor: fichaCliente.nomeDoDevedor,
      values: {
        data: toBrazilianDate(fichaCliente.data),
        processo: fichaCliente.processo,
        nomeDoDevedor: fichaCliente.nomeDoDevedor,
        valorPrincipal: getMoneyMask(fichaCliente.valorPrincipal),
        fase: fichaCliente.fase,
        motivoBaixa: fichaCliente.motivoBaixa,
      },
    };
  }, []);

  const tableContent = useMemo(
    () => listaFichaCliente.map((fichaCliente, index) => makeConteudoTabela(
      fichaCliente,
      index,
    )),
    [listaFichaCliente, makeConteudoTabela],
  );

  const changeDocumentoStringKey = useCallback((e) => {
    const { value } = e.target;
    setCpfCnpjDevedor({ ...cpfCnpjDevedor, value: e.target.value });
    setIsCnpj(value.length > 11 || /[a-zA-Z]/i.test(value));
  }, [setCpfCnpjDevedor, cpfCnpjDevedor]);

  const onChangeRadioHandler = useCallback((event) => {
    const { value } = event.target;
    if (value === radioOptions.TODOS || value === radioOptions.PROCESSOS_ATIVOS || value === radioOptions.FASE_ESPECIFICA) {
      setIdFase(new FieldModel({ name: 'idFase', value: '' }));
    }
    setDefinition(value);
  }, [setDefinition, setIdFase, radioOptions.TODOS, radioOptions.PROCESSOS_ATIVOS, radioOptions.FASE_ESPECIFICA]);

  const pesquisarFichaCliente = useCallback(() => {
    const payload = {
      idCliente,
      dataInicio: toAmericanDate(dataInicio.value),
      valorFinal: valorFinal.value,
      valorInicial: valorInicial.value,
      idProcesso: nroProcesso.value,
      idDevedor: idDevedor.value,
      codigoNoCliente: codNoCliente.value,
      cnpjCpf: cpfCnpjDevedor.value,
      nomeFantasia: nomeDevedor.value,
      cidadeDevedor: cidadeDevedor.value,
      idEstado: idEstado.value,
      idFase: idFase.value,
      idMotivoBaixa: idMotivoBaixa.value,
      status: definition,
    };
    buscarFichaCliente(payload).then((response) => {
      setListaFichaCliente(response.data);
    }).catch((error) => {
      const message = error.response ? error.response.data.message : 'Erro inesperado durante a pesquisa.';
      setListaFichaCliente([]);
      toast.warn(message);
    });
  }, [idCliente,
    dataInicio,
    valorFinal,
    valorInicial,
    nroProcesso,
    idDevedor,
    codNoCliente,
    cpfCnpjDevedor,
    nomeDevedor,
    cidadeDevedor,
    idEstado,
    idFase,
    idMotivoBaixa,
    definition]);

  const limparFiltros = useCallback(() => {
    setDataInicio(new FieldModel({ name: 'dataEnvioInicial', type: DATE, value: toBrazilianDate(new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)) }));
    setValorFinal(new FieldModel({ name: 'valorFinal', value: '9999999999.99' }));
    setValorInicial(new FieldModel({ name: 'valorInicial', value: '0' }));
    setNroProcesso(new FieldModel({ name: 'nroProcesso', value: '' }));
    setIdDevedor(new FieldModel({ name: 'idDevedor', value: '' }));
    setCodNoCliente(new FieldModel({ name: 'codNoCliente', value: '' }));
    setCpfCnpjDevedor(new FieldModel({ name: 'cpfCnpjDevedor', value: '' }));
    setIsCnpj(false);
    setNomeDevedor(new FieldModel({ name: 'nomeDevedor', value: '' }));
    setCidadeDevedor(new FieldModel({ name: 'cidadeDevedor', value: '' }));
    setIdEstado(new FieldModel({ name: 'idEstado', value: '' }));
    setIdFase(new FieldModel({ name: 'idFase', value: '' }));
    setIdMotivoBaixa(new FieldModel({ name: 'idMotivoBaixa', value: '' }));
    setDefinition(radioOptions.TODOS);
  }, [radioOptions.TODOS]);

  const fetchFasesData = async () => {
    const responseFaseBaixa = await buscarIdFaseBaixa();
    setIdFaseBaixa(responseFaseBaixa.data);
  };

  const imprimirFichaCliente = useCallback(() => {
    const payload = {
      idCliente,
      dataInicio: toAmericanDate(dataInicio.value),
      valorFinal: valorFinal.value,
      valorInicial: valorInicial.value,
      idProcesso: nroProcesso.value,
      idDevedor: idDevedor.value,
      codigoNoCliente: codNoCliente.value,
      cnpjCpf: cpfCnpjDevedor.value,
      nomeFantasia: nomeDevedor.value,
      cidadeDevedor: cidadeDevedor.value,
      idEstado: idEstado.value,
      idFase: idFase.value,
      idMotivoBaixa: idMotivoBaixa.value,
      status: definition,
    };
    exportarFichaCliente(payload).then((response) => {
      if (response.data.length > 0) {
        downloadFileSispag(response.headers.filename, response.data);
      }
    }).catch(() => {
      toast.warn('Nenhum resultado foi encontrado relacionado à sua pesquisa.');
    });
  }, [definition, idCliente, dataInicio, valorFinal, valorInicial, nroProcesso, idDevedor, codNoCliente, cpfCnpjDevedor, nomeDevedor, cidadeDevedor, idEstado, idFase, idMotivoBaixa]);


  const handleInspecionar = useCallback((idProcesso) => {
    localStorage.setItem('previousUrl', '');
    window.open(`/processos/inspecao?nroProcesso=${idProcesso}`, '_blank');
  }, []);

  const handleAcompanhamento = useCallback(async (idProcesso, nome) => {
    setDialogAcompanhamento(prev => ({
      ...prev, open: true, loading: true, titulo: `${idProcesso} - ${nome}`,
    }));
    try {
      const { data } = await buscarAcompanhamentoDoProcessoService(idProcesso);
      setDialogAcompanhamento(prev => ({ ...prev, loading: false, dados: data }));
    } catch {
      toast.error('Erro ao buscar acompanhamento do processo.');
      setDialogAcompanhamento(prev => ({ ...prev, open: false, loading: false }));
    }
  }, []);

  useEffect(() => {
    globalDispatch(findEstadosAction());
    globalDispatch(findFasesAction());
    globalDispatch(findMotivosBaixaAction());

    fetchFasesData();
  }, [globalDispatch]);

  useEffect(() => {
    if (definition === radioOptions.PROCESSOS_BAIXADOS) {
      setIdFase(prev => ({
        ...prev,
        value: idFaseBaixa,
      }));
    }

    if (definition !== radioOptions.FASE_ESPECIFICA) {
      setIdMotivoBaixa(prev => ({
        ...prev,
        value: '',
      }));
    }
  }, [definition, idFaseBaixa, radioOptions.FASE_ESPECIFICA, radioOptions.PROCESSOS_BAIXADOS]);

  return (
    <>
      <Grid24 container isFormContainer xs={24} sm={24}>
        <Grid24 container isFormContainer xs={24} sm={15}>
          <section style={{
            border: 'solid',
            borderWidth: 'thin',
            padding: '6px',
            paddingLeft: '10px',
            paddingTop: '4px',
            color: '#707070',
            width: '100%',
            cursor: 'default',
            display: 'flex',
            gap: '10px',
          }}
          >
            <FieldsContainer>
              <Grid24 container isFormContainer spacing={1}>
                <Grid24
                  item
                  xs={24}
                  sm={4}
                  style={{
                    display: 'flex', alignItems: 'center', marginTop: '16px', minWidth: '25%',
                  }}
                >
                  <DateField
                    isForm
                    fullWidth
                    label="Abertura Após:"
                    maxDate={new Date()}
                    disabled={isInsertMode}
                    name={dataInicio.name}
                    value={dataInicio.value}
                    onChange={e => setDataInicio({
                      ...dataInicio,
                      value: e.target.value,
                    })}
                  />
                </Grid24>
                <Grid24
                  xs={24}
                  sm={3}
                  style={{
                    display: 'flex', alignItems: 'center', marginTop: '16px', justifyContent: 'end', minWidth: '15%',
                  }}
                >
                  <span style={{
                    fontSize: '12px',
                    alignSelf: 'flex-end',
                    marginRight: '10px',
                    color: 'gray',
                  }}
                  >
                    Valor Principal:
                  </span>
                </Grid24>
                <Grid24 xs={24} sm={4} style={{ marginTop: '16px', minWidth: '28%' }}>
                  <MoneyField
                    isForm
                    fullWidth
                    disabled={isInsertMode}
                    maxlength={16}
                    label="De:"
                    name={valorInicial.name}
                    value={valorInicial.value}
                    onChange={e => setValorInicial({ ...valorInicial, value: e.target.value })}
                  />
                </Grid24>
                <Grid24 xs={24} sm={4} style={{ marginTop: '16px', minWidth: '28%' }}>
                  <MoneyField
                    isForm
                    fullWidth
                    disabled={isInsertMode}
                    maxlength={16}
                    label="Até:"
                    name={valorFinal.name}
                    value={valorFinal.value}
                    onChange={e => setValorFinal({ ...valorFinal, value: e.target.value })}
                  />
                </Grid24>
              </Grid24>
              <Grid24 container isFormContainer spacing={1}>
                <Grid24 xs={24} sm={4} style={{ marginTop: '10px', minWidth: '24%' }}>
                  <NumberInput
                    isForm
                    fullWidth
                    numberType="integer"
                    disabled={isInsertMode}
                    maxValue={999999999}
                    name={nroProcesso.name}
                    label="Número do Processo"
                    value={nroProcesso.value}
                    acceptEmptyValue
                    onChange={e => setNroProcesso({ ...nroProcesso, value: e.target.value })}
                  />
                </Grid24>
                <Grid24 xs={24} sm={4} style={{ marginTop: '10px', marginLeft: '10px', minWidth: '16%' }}>
                  <NumberInput
                    isForm
                    fullWidth
                    numberType="integer"
                    disabled={isInsertMode}
                    maxValue={999999999}
                    name={idDevedor.name}
                    label="Cód. Devedor"
                    value={idDevedor.value}
                    acceptEmptyValue
                    onChange={e => setIdDevedor({ ...idDevedor, value: e.target.value })}
                  />
                </Grid24>
                <Grid24 xs={24} sm={5} style={{ marginTop: '10px', marginLeft: '10px', minWidth: '26%' }}>
                  <NumberInput
                    isForm
                    fullWidth
                    numberType="integer"
                    disabled={isInsertMode}
                    maxValue={999999999}
                    name={codNoCliente.name}
                    label="Cód. Devedor no Cliente"
                    value={codNoCliente.value}
                    acceptEmptyValue
                    onChange={e => setCodNoCliente({ ...codNoCliente, value: e.target.value })}
                  />
                </Grid24>
                <Grid24 xs={24} sm={4} style={{ marginTop: '10px', marginLeft: '10px', minWidth: '25%' }}>
                  {isCnpj ? (
                    <AlphanumericMaskField
                      isForm
                      fullWidth
                      label="CPF/CNPJ Devedor"
                      disabled={isInsertMode}
                      name={cpfCnpjDevedor.name}
                      value={cpfCnpjDevedor.value}
                      onChange={changeDocumentoStringKey}
                      format={`${CNPJ_MASK}`}
                    />
                  ) : (
                    <AlphanumericMaskField
                      isForm
                      fullWidth
                      label="CPF/CNPJ Devedor"
                      disabled={isInsertMode}
                      name={cpfCnpjDevedor.name}
                      value={cpfCnpjDevedor.value}
                      onChange={changeDocumentoStringKey}
                      format={`${CPF_MASK}#`}
                    />
                  )}
                </Grid24>
              </Grid24>
              <Grid24 container isFormContainer spacing={1}>
                <Grid24 xs={24} sm={5} style={{ marginTop: '10px', minWidth: '25%' }}>
                  <InputField
                    fullWidth
                    name={nomeDevedor.name}
                    disabled={isInsertMode}
                    isForm
                    value={nomeDevedor.value}
                    label="Nome do Devedor"
                    onChange={e => setNomeDevedor({ ...nomeDevedor, value: e.target.value })}
                    maxLength={60}
                  />
                </Grid24>
                <Grid24 xs={24} sm={5} style={{ marginTop: '10px', minWidth: '25%' }}>
                  <InputField
                    fullWidth
                    name={cidadeDevedor.name}
                    disabled={isInsertMode}
                    isForm
                    value={cidadeDevedor.value}
                    label="Cidade do Devedor"
                    onChange={e => setCidadeDevedor({ ...cidadeDevedor, value: e.target.value })}
                    maxLength={75}
                  />
                </Grid24>
                <Grid24 item xs={24} sm={4} style={{ marginTop: '10px', minWidth: '25%' }}>
                  <SelectorField
                    hasEmptyLabel
                    fullWidth
                    disabled={isInsertMode}
                    isForm
                    name="idEstado"
                    label="Estado do Devedor"
                    items={estados}
                    value={idEstado.value}
                    onChange={e => setIdEstado({ ...idEstado, value: e.target.value })}
                  />
                </Grid24>
              </Grid24>
              <WrapperSubTitleForm title="Status">
                <GroupDefinitions
                  aria-label="status-definicoes"
                  value={definition}
                  onChange={onChangeRadioHandler}
                >
                  <Grid24 container isFormContainer spacing={1}>
                    <Grid24 item xs={24} sm={4} style={{ marginTop: '10px', minWidth: '25%' }}>
                      <Radio label="Todos" value={radioOptions.TODOS} />
                    </Grid24>
                    <Grid24 item xs={24} sm={4} style={{ marginTop: '10px', minWidth: '25%' }}>
                      <Radio label="Fase específica" value={radioOptions.FASE_ESPECIFICA} />
                    </Grid24>
                    <Grid24 item xs={24} sm={4} style={{ marginTop: '10px', minWidth: '25%' }}>
                      <Radio label="Somente Processos Ativos" value={radioOptions.PROCESSOS_ATIVOS} />
                    </Grid24>
                    <Grid24 item xs={24} sm={4} style={{ marginTop: '10px', minWidth: '25%' }}>
                      <Radio label="Somente Processos Baixados" value={radioOptions.PROCESSOS_BAIXADOS} />
                    </Grid24>
                  </Grid24>
                </GroupDefinitions>
              </WrapperSubTitleForm>
              <Grid24 container isFormContainer spacing={1}>
                <Grid24 item xs={24} sm={5} style={{ marginTop: '10px', minWidth: '45%' }}>
                  <SelectorField
                    isForm
                    hasEmptyLabel
                    disabled={isInsertMode || definition !== radioOptions.FASE_ESPECIFICA}
                    label="Fase"
                    items={fases}
                    name={idFase.name}
                    value={idFase.value}
                    onChange={(e) => {
                      if (e.target.value !== idFaseBaixa) {
                        setIdMotivoBaixa({ ...idMotivoBaixa, value: '' });
                      }
                      setIdFase({ ...idFase, value: e.target.value });
                    }}
                  />
                </Grid24>
                <Grid24 item xs={24} sm={5} style={{ marginTop: '10px', minWidth: '45%' }}>
                  <SelectorField
                    isForm
                    hasEmptyLabel
                    disabled={isInsertMode || idFase.value !== idFaseBaixa}
                    label="Motivo da Baixa"
                    items={motivosBaixa}
                    name={idMotivoBaixa.name}
                    value={idMotivoBaixa.value}
                    onChange={e => setIdMotivoBaixa({ ...idMotivoBaixa, value: e.target.value })}
                  />
                </Grid24>
              </Grid24>

            </FieldsContainer>
            <ActionsContainer>
              <Grid24 container isFormContainer spacing={1}>
                <Grid24
                  xs={24}
                  sm={24}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    marginTop: '16px',
                  }}
                >
                  <SearchButtonWithLabel
                    style={{ width: '200px' }}
                    disabled={isInsertMode}
                    label="Pesquisar"
                    type="button"
                    onClick={() => pesquisarFichaCliente()}
                  />
                  <ClearButton
                    style={{ width: '200px' }}
                    disabled={isInsertMode}
                    onClick={() => limparFiltros()}
                    iconFontAwesome="fas fa-eraser"
                    label="Limpar"
                  />
                </Grid24>
              </Grid24>
            </ActionsContainer>
          </section>
        </Grid24>
        <Grid24 container isFormContainer xs={24} sm={8} style={{ flexFlow: 'column', marginLeft: '10px' }}>
          <Grid24 container isFormContainer>
            <FacilitadoresButton
              disabled={isInsertMode || !permission.hasAnyPermissionCCCliente}
              onClick={() => window.open(`/conta-corrente-cliente/pesquisar?idCliente=${idCliente}`, '_blank')}
            >
              C/C de Cliente
            </FacilitadoresButton>
          </Grid24>
          <Grid24 container isFormContainer>
            <FacilitadoresButton
              disabled={isInsertMode}
              onClick={() => { imprimirFichaCliente(); }}
            >
              Excel
            </FacilitadoresButton>
          </Grid24>
        </Grid24>
      </Grid24>
      <Grid24 container isFormContainer xs={24} sm={24} style={{ marginTop: '10px' }}>
        <TableFichaCliente
          columns={tableFichaClienteFactory.makeCabecalhoTable()}
          rows={tableContent}
          onInspecionar={handleInspecionar}
          onAcompanhamento={handleAcompanhamento}
        />
      </Grid24>
      <DialogNew
        open={dialogAcompanhamento.open}
        text={[dialogAcompanhamento.titulo]}
        closeIcon
        onClose={() => setDialogAcompanhamento(prev => ({ ...prev, open: false }))}
      >
        {dialogAcompanhamento.loading
          ? <Box display="flex" justifyContent="center"><CircularProgress /></Box>
          : (
            <AcompanhamentoProcesso
              dadosProcesso={dialogAcompanhamento.dados}
              disableEditarEndossante
            />
          )}
      </DialogNew>
    </>
  );
}

export default React.memo(FichaDoCliente);
